/**
 * AI Recruit - Question Type API Service
 * CRUD for proctored-interview question types and their sub-types.
 */

import { apiV2 } from './api'

export interface QuestionSubtype {
  id: string
  question_type_id: string
  name: string
  description?: string | null
  default_duration_minutes?: number | null
  is_active: boolean
  display_order: number
}

export interface QuestionType {
  id: string
  organization_id: string
  name: string
  description?: string | null
  category: string
  response_mode: 'verbal' | 'text' | 'code_editor' | 'whiteboard'
  default_duration_minutes: number
  is_active: boolean
  display_order: number
  created_at: string
  updated_at: string
  subtypes: QuestionSubtype[]
}

export interface QuestionTypeListResponse {
  question_types: QuestionType[]
  total: number
}

export const fetchQuestionTypes = async (includeInactive = false): Promise<QuestionType[]> => {
  const response = await apiV2.get<QuestionTypeListResponse>('/question-types', {
    params: { include_inactive: includeInactive },
  })
  return response.data.question_types
}

export const createQuestionType = async (data: {
  name: string
  description?: string
  category?: string
  response_mode?: string
  default_duration_minutes?: number
}): Promise<QuestionType> => {
  const response = await apiV2.post<QuestionType>('/question-types', data)
  return response.data
}

export const updateQuestionType = async (
  typeId: string,
  data: Partial<{
    name: string
    description: string
    category: string
    response_mode: string
    default_duration_minutes: number
  }>
): Promise<QuestionType> => {
  const response = await apiV2.put<QuestionType>(`/question-types/${typeId}`, data)
  return response.data
}

export const deactivateQuestionType = async (typeId: string): Promise<QuestionType> => {
  const response = await apiV2.post<QuestionType>(`/question-types/${typeId}/deactivate`)
  return response.data
}

export const activateQuestionType = async (typeId: string): Promise<QuestionType> => {
  const response = await apiV2.post<QuestionType>(`/question-types/${typeId}/activate`)
  return response.data
}

export const createSubtype = async (
  typeId: string,
  data: { name: string; description?: string; default_duration_minutes?: number }
): Promise<QuestionSubtype> => {
  const response = await apiV2.post<QuestionSubtype>(`/question-types/${typeId}/subtypes`, data)
  return response.data
}

export const updateSubtype = async (
  subtypeId: string,
  data: { name: string; description?: string; default_duration_minutes?: number }
): Promise<QuestionSubtype> => {
  const response = await apiV2.put<QuestionSubtype>(`/question-types/subtypes/${subtypeId}`, data)
  return response.data
}

export const deactivateSubtype = async (subtypeId: string): Promise<QuestionSubtype> => {
  const response = await apiV2.post<QuestionSubtype>(`/question-types/subtypes/${subtypeId}/deactivate`)
  return response.data
}

export const activateSubtype = async (subtypeId: string): Promise<QuestionSubtype> => {
  const response = await apiV2.post<QuestionSubtype>(`/question-types/subtypes/${subtypeId}/activate`)
  return response.data
}

export interface QuestionTypeAuditLogEntry {
  entity_type: 'question_type' | 'question_subtype'
  entity_name: string
  action: 'created' | 'updated' | 'activated' | 'deactivated'
  changed_by_name?: string | null
  details: Record<string, any>
  created_at: string
}

export const getQuestionTypeAuditLog = async (): Promise<QuestionTypeAuditLogEntry[]> => {
  const response = await apiV2.get<QuestionTypeAuditLogEntry[]>('/question-types/audit-log')
  return response.data
}
